import { ZuruflexApiError } from '@zuruflex/api-client';
import { REVIEW_REPORT_REASONS, type Review } from '@zuruflex/types';
import { formatRatingIn, toLocale } from '@zuruflex/i18n';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  FormField,
  PageHeader,
  RatingStars,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GuestReviewDialog } from '../components/GuestReviewDialog.js';
import { api } from '../lib/api.js';

/**
 * Everything an owner has to do with reviews, in one place.
 *
 * Three tabs rather than three pages: what customers said, who is still owed
 * a review, and what this partner wrote. An owner clearing a backlog wants
 * them together, and the bookings page can only ever show the second.
 */
export function ReviewsPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const [reporting, setReporting] = useState<Review | null>(null);
  const [reviewing, setReviewing] = useState<
    { reference: string; guestName: string; vehicle: string } | null
  >(null);

  const mine = useQuery({
    queryKey: ['partner-reviews'],
    queryFn: () => api.listReviews({ perPage: 100 }),
  });

  const owed = useQuery({
    queryKey: ['guests-to-review'],
    queryFn: () => api.pendingGuestReviews({ perPage: 100 }),
  });

  const aboutCars = (mine.data?.items ?? []).filter(
    (review) => review.side === 'GUEST_ON_LISTING',
  );
  const written = (mine.data?.items ?? []).filter((review) => review.side === 'HOST_ON_GUEST');

  const summary = (() => {
    const scored = aboutCars.filter((review) => review.status === 'PUBLISHED');
    if (scored.length === 0) return null;
    const sum = scored.reduce((total, review) => total + review.overall, 0);
    return { average: Math.round((sum * 100) / scored.length), count: scored.length };
  })();

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('reviews.title')}
        description={t('reviews.subtitle')}
        action={
          summary ? (
            <div className="flex items-center gap-2">
              <RatingStars value={summary.average / 100} size="sm" />
              <span className="text-lg font-semibold">
                {formatRatingIn(summary.average, locale)}
              </span>
              <span className="text-sm text-text-muted">
                {t('common:rating.count', { count: summary.count })}
              </span>
            </div>
          ) : null
        }
      />

      {/* Owners ask where the number came from; answering here is cheaper
          than answering it in support. */}
      <p className="text-sm text-text-muted">{t('reviews.derivedHint')}</p>

      <Tabs defaultValue="cars">
        <TabsList>
          <TabsTrigger value="cars">{t('reviews.tab.cars')}</TabsTrigger>
          <TabsTrigger value="owed">
            {t('reviews.tab.owed')}
            {owed.data?.total ? (
              <Badge variant="brand" className="ml-2">
                {owed.data.total}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="written">{t('reviews.tab.written')}</TabsTrigger>
        </TabsList>

        <TabsContent value="cars" className="pt-4">
          {mine.error ? (
            <ErrorState error={mine.error} onRetry={() => void mine.refetch()} />
          ) : mine.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : aboutCars.length === 0 ? (
            <EmptyState title={t('reviews.noneAboutCars')} description={t('reviews.noneBody')} />
          ) : (
            <ul className="flex flex-col gap-3">
              {aboutCars.map((review) => (
                <li
                  key={review.id}
                  className="rounded-card border border-border-default bg-surface-raised p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <RatingStars value={review.overall} size="sm" />
                        <span className="font-semibold">{review.subjectName}</span>
                        <span className="font-mono text-xs text-text-muted">
                          {review.bookingReference}
                        </span>
                        {review.status !== 'PUBLISHED' ? (
                          <Badge variant="warning">
                            {t(`common:rating.state.${review.status}`)}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-1 text-sm text-text-muted">
                        {t('reviews.by', { name: review.authorName })}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setReporting(review)}>
                      {t('reviews.report')}
                    </Button>
                  </div>
                  {review.body ? (
                    <p className="mt-3 whitespace-pre-line text-sm">{review.body}</p>
                  ) : null}
                  {review.hiddenReason ? (
                    <Alert variant="warning" className="mt-3">
                      {t('reviews.hiddenBecause', { reason: review.hiddenReason })}
                    </Alert>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="owed" className="pt-4">
          {owed.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : (owed.data?.items.length ?? 0) === 0 ? (
            <EmptyState title={t('reviews.noneOwed')} description={t('reviews.noneOwedBody')} />
          ) : (
            <ul className="flex flex-col gap-3">
              {owed.data!.items.map((hire) => (
                <li
                  key={hire.reference}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-border-default bg-surface-raised p-4"
                >
                  <div className="min-w-0">
                    <p className="font-semibold">
                      {hire.customer.fullName ?? t('common:field.customer')}
                    </p>
                    <p className="text-sm text-text-muted">
                      {hire.vehicle.year} {hire.vehicle.make} {hire.vehicle.model} ·{' '}
                      <span className="font-mono text-xs">{hire.reference}</span>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() =>
                      setReviewing({
                        reference: hire.reference,
                        guestName: hire.customer.fullName ?? t('common:field.customer'),
                        vehicle: `${hire.vehicle.make} ${hire.vehicle.model}`,
                      })
                    }
                  >
                    {t('reviews.reviewGuest')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="written" className="pt-4">
          {written.length === 0 ? (
            <EmptyState title={t('reviews.noneWritten')} description={t('reviews.noneBody')} />
          ) : (
            <ul className="flex flex-col gap-3">
              {written.map((review) => (
                <li
                  key={review.id}
                  className="rounded-card border border-border-default bg-surface-raised p-4"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <RatingStars value={review.overall} size="sm" />
                    <span className="font-semibold">{review.subjectName}</span>
                    {!review.revealed ? (
                      <Badge variant="default">{t('reviews.awaitingReveal')}</Badge>
                    ) : null}
                  </div>
                  {review.body ? (
                    <p className="mt-2 whitespace-pre-line text-sm text-text-muted">
                      {review.body}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>

      {reporting ? (
        <ReportDialog review={reporting} onClose={() => setReporting(null)} />
      ) : null}

      {reviewing ? (
        <GuestReviewDialog
          open
          onOpenChange={(next) => {
            if (!next) setReviewing(null);
          }}
          booking={reviewing}
        />
      ) : null}
    </div>
  );
}

/**
 * Objecting to a review.
 *
 * The dialog says plainly that the review stays up while staff look at it.
 * An owner who expects a report to delete something will report everything
 * and then escalate when nothing disappears.
 */
function ReportDialog({ review, onClose }: { review: Review; onClose: () => void }) {
  const { t } = useTranslation(['partner', 'common']);
  const queryClient = useQueryClient();
  const [reason, setReason] = useState<string>('UNTRUE');
  const [detail, setDetail] = useState('');

  const report = useMutation({
    mutationFn: () => api.reportReview(review.id, { reason, detail: detail.trim() || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partner-reviews'] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('reviews.reportTitle')}</DialogTitle>
          <DialogDescription>{t('reviews.reportStaysUp')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label={t('reviews.reportReason')}>
            {(field) => (
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger id={field.id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REVIEW_REPORT_REASONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`common:rating.reportReason.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>

          {/* Required, because a ground with no facts behind it is not a case
              staff can act on. */}
          <FormField label={t('reviews.reportDetail')} hint={t('reviews.reportDetailHint')}>
            {(field) => (
              <Textarea
                id={field.id}
                rows={4}
                value={detail}
                onChange={(event) => setDetail(event.target.value)}
              />
            )}
          </FormField>

          {report.isError ? (
            <Alert variant="danger">
              {report.error instanceof ZuruflexApiError
                ? report.error.message
                : t('common:error.unexpected')}
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="destructive"
            isLoading={report.isPending}
            disabled={!detail.trim()}
            onClick={() => report.mutate()}
          >
            {t('reviews.reportSend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
